import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { I18nService } from '../../../core';
import { BillingGateService } from '../../../core/billing-gate';
import { TenantService } from '../../../core/tenant/tenant.service';
import type { SidebarProjectNode } from '../models/sidebar.models';
import { SidebarApiService } from '../services/sidebar-api.service';
import { isCompanyChangedAbort, runWithTenantRetries } from '../../../core/utils/tenant-retries';

@Injectable({ providedIn: 'root' })
export class SidebarStore {
  private readonly i18n = inject(I18nService);
  private readonly tenant = inject(TenantService);
  private readonly api = inject(SidebarApiService);
  private readonly billingGate = inject(BillingGateService);

  private readonly cacheByCompany = new Map<string, SidebarProjectNode[]>();
  private readonly inFlightByCompany = new Map<string, Promise<SidebarProjectNode[]>>();

  private readonly loadedCompanyIdSignal = signal<string | null>(null);
  private readonly projectsSignal = signal<SidebarProjectNode[]>([]);
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);

  readonly projects = this.projectsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly isEmpty = computed(
    () => !this.loadingSignal() && !this.errorSignal() && this.projectsSignal().length === 0
  );

  constructor() {
    effect(() => {
      // Depender de `activeCompany()` (não só do id): após login, `setCompanies`/memberships pode
      // substituir o objeto com o mesmo id e precisamos de nova tentativa se o 1.º GET falhou.
      void this.tenant.activeCompany();
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.resetSidebar();
        return;
      }
      /** Sucesso (ou cache) para esta empresa — não repetir em loop. Em erro não marcamos loaded. */
      if (this.loadedCompanyIdSignal() === companyId) return;
      void this.loadSidebar();
    });
  }

  async loadSidebar(options?: { force?: boolean }): Promise<void> {
    const force = options?.force === true;
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) {
      this.resetSidebar();
      return;
    }

    if (!force) {
      const cached = this.cacheByCompany.get(requestCompanyId);
      if (cached) {
        this.loadedCompanyIdSignal.set(requestCompanyId);
        this.projectsSignal.set(cached);
        this.loadingSignal.set(false);
        this.errorSignal.set(null);
        return;
      }
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    let request = this.inFlightByCompany.get(requestCompanyId);
    if (!request || force) {
      request = this.fetchSidebarProjectsWithRetries(requestCompanyId);
      this.inFlightByCompany.set(requestCompanyId, request);
    }

    try {
      const projects = await request;
      if (this.tenant.companyId() !== requestCompanyId) return;
      this.cacheByCompany.set(requestCompanyId, projects);
      this.loadedCompanyIdSignal.set(requestCompanyId);
      this.projectsSignal.set(projects);
    } catch (e) {
      if (this.tenant.companyId() !== requestCompanyId) return;
      if (isCompanyChangedAbort(e)) return;
      // Com billing gate ativo, o interceptor suprime requisições fora da allowlist.
      // Tratar como vazio (e não como erro) para não poluir a sidebar enquanto o
      // usuário é guiado para o faturamento.
      if (this.billingGate.blocked()) {
        this.projectsSignal.set([]);
        this.errorSignal.set(null);
      } else {
        this.errorSignal.set(this.i18n.t('layout.sidebar.loadError'));
        this.projectsSignal.set([]);
      }
    } finally {
      if (this.inFlightByCompany.get(requestCompanyId) === request) {
        this.inFlightByCompany.delete(requestCompanyId);
      }
      if (this.tenant.companyId() !== requestCompanyId) return;
      this.loadingSignal.set(false);
    }
  }

  resetSidebar(): void {
    this.cacheByCompany.clear();
    this.inFlightByCompany.clear();
    this.loadedCompanyIdSignal.set(null);
    this.projectsSignal.set([]);
    this.loadingSignal.set(false);
    this.errorSignal.set(null);
  }

  private fetchSidebarProjectsWithRetries(companyId: string): Promise<SidebarProjectNode[]> {
    return runWithTenantRetries(companyId, () => this.tenant.companyId(), () => this.api.getSidebarProjects());
  }
}
